import { FC, useMemo } from 'react';

import AppointmentTypeIndicator from '@/components/AppointmentTypeIndicator.tsx';
import Card from '@/components/Card.tsx';
import { FormsStatusIcon } from '@/components/forms/forms-status-icon.tsx';
import AppointmentCardHeader from '@/components/room-view/AppointmentCard/AppointmentCardHeader.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import useCheckInData from '@/hooks/useCheckInData.ts';
import { usePatientAppointmentForms } from '@/hooks/usePatientAppointmentForms.ts';
import usePrismic from '@/hooks/usePrismic.tsx';

import { PrismicDataType } from '@/models/PrismicHookTypes.ts';
import { DailyAppointment, Priority } from '@/models/appointment/Appointment.ts';
import { UserAppointmentDto } from '@/models/call-system/dtos/user-appointment-dto.ts';

import { useAppointmentCheckinStore } from '@/stores/appointmentCheckinStore.ts';

import { getInstanceId, getPvsPatientId } from '@/utils/appointmentUtils.ts';
import { MISSING_PATIENT_ID, getInsuranceByKey, getPatientDisplayName } from '@/utils/patientUtils.ts';
import { getTextFromRichTextField } from '@/utils/prismicUtils.ts';

interface Props {
    appointment: DailyAppointment;
    checkInData?: UserAppointmentDto;
    onClick: (appointment: DailyAppointment) => void;
    onMutateAppointments: () => void;
}

const PendingAppointmentCard: FC<Props> = ({ appointment, onClick, checkInData, onMutateAppointments }) => {
    const insurance = useMemo(() => getInsuranceByKey(appointment.data.insurance_coverage_type), [appointment]);
    const { data: professional_profiles } = usePrismic(PrismicDataType.PROFESSIONAL_PROFILE);
    const { formatted_forms_for_appointment, forms_resolution } = usePatientAppointmentForms(appointment);
    const { mutate: mutateCheckinData } = useCheckInData();

    const { appointments: appointments_checkin } = useAppointmentCheckinStore();

    // Missing patient id are schedule created on DC without any patient attached. We don't support them.
    const missing_patient_id: boolean =
        !getPvsPatientId(appointment) || getPvsPatientId(appointment) === MISSING_PATIENT_ID;
    const displayName = getPatientDisplayName(appointment);
    const loading_patient: boolean =
        appointments_checkin[appointment.id]?.is_checking_in || (!missing_patient_id && !displayName);
    const disabled: boolean = loading_patient;

    const practitioner =
        getTextFromRichTextField(professional_profiles?.get(appointment.professional.key)?.display_name) ??
        appointment.professional.key;

    return (
        <Card
            data={appointment}
            instanceId={getInstanceId(appointment) ?? ''}
            disabled={disabled}
            onClick={onClick}
            priority={appointment.priority ?? Priority.NONE}
            tooltipText={missing_patient_id && !loading_patient ? 'Fügen Sie einen Patienten hinzu' : undefined}
        >
            <AppointmentCardHeader
                patientName={displayName}
                insurance={insurance}
                isInsuranceCardActive={appointment.data.is_insurance_card_active}
                date={appointment.start}
                showDate
                loading={loading_patient}
                checkinData={checkInData}
                isOnline={appointment.is_online}
                appointment={appointment}
                onTicketUpdate={async () => {
                    await mutateCheckinData();
                    onMutateAppointments();
                }}
            />
            <div className="flex items-center">
                <div className="min-w-0 flex-shrink flex-grow basis-0">
                    <Paragraph className="break-words text-sm xl:text-base">
                        {appointment.data.services[0]?.name}
                    </Paragraph>
                    <Paragraph className="text-sm text-slate-500 xl:text-base">{practitioner}</Paragraph>
                </div>
                <FormsStatusIcon
                    formsResolution={forms_resolution}
                    formattedFormsForAppointment={formatted_forms_for_appointment}
                    size="w-4 xl:w-5"
                />
            </div>
            <AppointmentTypeIndicator appointment={appointment} />
        </Card>
    );
};

export default PendingAppointmentCard;
